/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* eslint-disable security/detect-object-injection */
import * as React from 'react';

import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import DropDown from './components/dropDown';
import LinksComponent from './components/links';
import { IDobbiesAccountMenuData } from './dobbies-account-menu.data';
import { IDobbiesAccountMenuProps } from './dobbies-account-menu.props.autogenerated';

export interface IDobbiesAccountMenuViewProps extends IDobbiesAccountMenuProps<{}> {
    links?: React.ReactNode;
    contentBlockAnchorTag?: INodeProps;
    className?: string;
    contentBlockContainer: IModuleProps;
    dropDownLinks: React.ReactNode;
}

/**
 *
 * DobbiesAccountMenu component
 * @extends {React.PureComponent<IDobbiesAccountMenuProps<IDobbiesAccountMenuData>>}
 */
class DobbiesAccountMenu extends React.PureComponent<IDobbiesAccountMenuProps<IDobbiesAccountMenuData>> {
    private telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );
    public render(): JSX.Element | null {
        const { links, className } = this.props.config;
        const { resources, context } = this.props;
        const { loyaltyCard } = this.props.data;
        /* VSI-Customization -- START By default dobbies club text will be rendered as link with loyalty join link  */

        // by default dobbies club marketing page link
        let url = Msdyn365.getUrlSync('dobbiesClubMarketing', this.props.context.actionContext) || '';
        const dobbiesClubLink = {
            linkText: this.props.config.dobbiesClubLinkText,
            linkUrl: { destinationUrl: url, type: 'internalLink' },
            ariaLabel: this.props.config.dobbiesClubLinkText
        };
        /* VSI-Customization -- END  */

        /* VSI-Customization -- START If user has loyalty card then change the URL from loyalty-join to loyalty  */
        if (loyaltyCard && loyaltyCard.result && loyaltyCard.result.CardNumber) {
            dobbiesClubLink.ariaLabel = this.props.config.dobbiesClubLinkText;
            dobbiesClubLink.linkText = this.props.config.dobbiesClubLinkText;
            url = Msdyn365.getUrlSync('loyalty', context.actionContext) || '';
            dobbiesClubLink.linkUrl.destinationUrl = url;
        }
        /* VSI-Customization -- END  */

        // Push the dobbies club link into links array coming from CMS
        // If link doesn't exist in links array push it inside array
        if (links && links.length > 0) {
            // splice(index, 0, value)
            const linkPosition = (this.props.config.dobbiesClubLinkPosition || 2) - 1;
            const index = links.findIndex(x => x.linkText?.toLowerCase() === this.props.config.dobbiesClubLinkText?.toLowerCase());
            if (index < 0) {
                links.splice(linkPosition, 0, dobbiesClubLink);
            }
        }

        const contentBlockLinks = links && links.length > 0 && (
            <LinksComponent
                {...{
                    links: links,
                    onTextChange: this.handleLinkTextChange,
                    requestContext: this.props.context.request,
                    telemetryContent: this.telemetryContent,
                    url: context.request.canonicalUrl!
                }}
            />
        );
        const dropDownLinks = links && links.length > 0 && (
            <DropDown heading={resources.headingTextForMobile} url={context.request.canonicalUrl!} links={links} />
        );
        const accountMenuViewProps = {
            ...this.props,
            links: contentBlockLinks,
            dropDownLinks,
            moduleClass: this.props.config.className,
            contentBlockContainer: {
                moduleProps: this.props,
                className: classnames('ms-account-menu', className)
            },
            contentBlockAnchorTag: {
                tag: 'a',
                className: 'ms-account-menu__link',
                role: 'link'
            }
        };
        return this.props.renderView(accountMenuViewProps);
    }
    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.links && this.props.config.links[linkIndex]) {
            this.props.config.links[linkIndex].linkText = event.target.value;
        }
    };
}

export default DobbiesAccountMenu;
